import React, { useState, useMemo } from 'react';
import { Button, FormControl, InputLabel, Select, TextField } from '@material-ui/core';
import { Calibration, models } from '@balljames/calibration';
import { TCalibration } from '@balljames/calibration/build/components/Calibration';
import styled from 'styled-components';

interface Props {}

const CalibrationView: React.FunctionComponent<Props> = (props) => {
  const [calibration, setCalibration] = useState<TCalibration>();
  const [image, setImage] = useState<HTMLImageElement>();
  const [cameraType, setCameraType] = useState('Universal');
  const [model, setModel] = useState('full');
  const [width, setWidth] = useState('68');
  const [length, setLength] = useState('105');
  const [goalWidth, setGoalWidth] = useState('5');

  const saveCalibration = (calib: TCalibration) => {
    setCalibration(calib);
  };

  const handleChange = (e: any) => {
    const reader = new FileReader();

    reader.readAsDataURL(e.target.files[0]);

    reader.onloadend = (loadedFIle: any) => {
      var img = new Image();

      img.src = loadedFIle.target.result;
      img.onload = () => {
        setImage(img);
      };
    };
  };

  const m = useMemo(() => {
    if (model === 'futsaltopofbox') {
      return models.FutsalModelTopOfBox(parseFloat(width), parseFloat(length));
    }
    if (model === 'futsal15') {
      return models.FutsalModel15(parseFloat(width), parseFloat(length), parseFloat(goalWidth));
    }
    if (model === 'futsalextended') {
      return models.FutsalModelExtended(parseFloat(width), parseFloat(length));
    }
    if (model === 'futsal') {
      return models.FutsalModel(parseFloat(width), parseFloat(length));
    }
    if (model === 'fullextended') {
      return models.FullPitchExtendedModel(parseFloat(width), parseFloat(length));
    }
    return models.FullPitchModel(parseFloat(width), parseFloat(length));
  }, [model, width, length, goalWidth]);

  return (
    <div className="sciui-grid">
      <div className="sciui-grid-item-m-1">
        <Container className="sciui-card sciui-m-l sciui-p-m sciui-bg-p-5">
          <InputContainer>
            <Button variant="contained" component="label">
              1. Upload screenshot
              <input type="file" style={{ display: 'none' }} accept="image/*" onChange={handleChange} />
            </Button>
          </InputContainer>
          <InputContainer>
            <FormControl style={{ minWidth: '130px' }}>
              <InputLabel htmlFor="camera-type">2. Camera type</InputLabel>
              <Select
                native
                value={cameraType}
                fullWidth
                onChange={(e: any) => setCameraType(e.target.value)}
                inputProps={{
                  name: 'camera-type',
                  id: 'camera-type',
                }}>
                <option value="KickID">KickID</option>
                <option value="Heracles">Heracles</option>
                <option value="Arsenal">Arsenal</option>
                <option value="unknown">unknown</option>
                <option value="Universal">Universal</option>
              </Select>
            </FormControl>
          </InputContainer>
          <InputContainer>
            <FormControl style={{ minWidth: '130px' }}>
              <InputLabel htmlFor="model">3. Model</InputLabel>
              <Select
                native
                value={model}
                fullWidth
                onChange={(e: any) => setModel(e.target.value)}
                inputProps={{
                  name: 'model',
                  id: 'model',
                }}>
                <option value="futsal15">Futsal 15</option>
                <option value="futsaltopofbox">Futsal top of box</option>
                <option value="futsalextended">Futsal extended</option>
                <option value="futsal">Futsal</option>
                <option value="full">Full pitch</option>
                <option value="fullextended">Full pitch extended</option>
              </Select>
            </FormControl>
          </InputContainer>
          <InputContainer>
            <FormControl style={{ minWidth: '130px' }}>
              <TextField
                margin="dense"
                id="pitch-length"
                label="4. Pitch length (m)"
                type="text"
                fullWidth
                value={length}
                onChange={(event) => setLength(event.target.value)}
              />
            </FormControl>
          </InputContainer>
          <InputContainer>
            <FormControl style={{ minWidth: '130px' }}>
              <TextField
                margin="dense"
                id="pitch-width"
                label="5. Pitch width (m)"
                type="text"
                fullWidth
                value={width}
                onChange={(event) => setWidth(event.target.value)}
              />
            </FormControl>
          </InputContainer>
          {model === 'futsal15' && length && width && (
            <InputContainer>
              <FormControl style={{ minWidth: '130px' }}>
                <TextField
                  margin="dense"
                  id="goal-width"
                  label="Goal width (m)"
                  type="text"
                  fullWidth
                  value={goalWidth}
                  onChange={(event) => setGoalWidth(event.target.value)}
                />
              </FormControl>
            </InputContainer>
          )}
          {model && cameraType && image && (
            <InputContainer>
              <p>
                Now position the pitch markers over the screenshot. If there are markers that are not in the screenshot
                then position them outside the image. When you are done press 'c' to start calibration and then press
                's' to save calibration.json file.
              </p>
            </InputContainer>
          )}
          {calibration && (
            <InputContainer>
              <Button
                variant="contained"
                href={'data:application/json;base64,' + btoa(JSON.stringify(calibration))}
                download="calibration.json">
                Download
                {/* <a
                  download="calibration.json"
                  style={{ display: 'none' }}
                  href={'data:application/json;base64,' + btoa(JSON.stringify(calibration))}>
                  Download
                </a> */}
              </Button>
            </InputContainer>
          )}
        </Container>
      </div>
      <div className="sciui-grid-item-m-11">
        <Container className="sciui-card sciui-m-l sciui-bg-p-5">
          {image && (
            <Calibration
              baseUrl={process.env.REACT_APP_CALIBRATION_URL}
              model={m}
              image={image}
              saveCalibration={saveCalibration}
              cameraType={cameraType}
            />
          )}
        </Container>
      </div>
    </div>
  );
};

const Container = styled.div`
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  margin-bottom: 20px;
`;

export default CalibrationView;
